// src/pages/PBSurveyAdmin.tsx: PB admin survey management (is_super_admin only).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../services/supabase';
import {
  C, PulseSurvey, PulseQuestion, PulseTemplate, QuestionType, TargetAudience,
  QUESTION_TYPES, AUDIENCE_OPTIONS, normalizeQuestion, fmtDate,
  notifySurveyPublished, notifyResultsPublished,
} from '../lib/pulse';
import {
  card, inputStyle, labelStyle, btnPrimary, btnGold, btnGhost, Banner, LoadingScreen, StatusBadge, QuestionInput,
} from '../lib/pulseUI';
import ResultsDashboard from '../lib/ResultsDashboard';

interface ProfileLite { id: string; full_name: string | null; email: string | null; role: string; }
interface SurveyRow extends PulseSurvey { responseCount: number; completeCount: number; assignmentCount: number; }

type Tab = 'surveys' | 'create' | 'templates' | 'results';

let LID = 0;
const nextId = () => `q${++LID}`;

interface BQ {
  localId: string;
  id?: string;
  question_type: QuestionType;
  question_text: string;
  description: string;
  required: boolean;
  options: string[];
  matrix_rows: string[];
  matrix_columns: string[];
  rating_min: number;
  rating_max: number;
  rating_min_label: string;
  rating_max_label: string;
}

function blankQuestion(type: QuestionType): BQ {
  return {
    localId: nextId(), question_type: type, question_text: '', description: '', required: true,
    options: type === 'multiple_choice' || type === 'multi_select' || type === 'ranking' ? ['Option 1', 'Option 2'] : [],
    matrix_rows: type === 'matrix' ? ['Row 1', 'Row 2'] : [],
    matrix_columns: type === 'matrix' ? ['1', '2', '3', '4', '5'] : [],
    rating_min: 1, rating_max: 5, rating_min_label: '', rating_max_label: '',
  };
}

function toBQ(q: PulseQuestion): BQ {
  return {
    localId: nextId(), id: q.id, question_type: q.question_type, question_text: q.question_text,
    description: q.description || '', required: q.required, options: q.options, matrix_rows: q.matrix_rows,
    matrix_columns: q.matrix_columns, rating_min: q.rating_min, rating_max: q.rating_max,
    rating_min_label: q.rating_min_label || '', rating_max_label: q.rating_max_label || '',
  };
}

// A BQ as a display-only PulseQuestion for preview rendering.
function bqToPreview(b: BQ, i: number): PulseQuestion {
  return {
    id: b.localId, survey_id: '', order_index: i, question_type: b.question_type, question_text: b.question_text || '(untitled question)',
    description: b.description || null, required: b.required, options: b.options, matrix_rows: b.matrix_rows,
    matrix_columns: b.matrix_columns, rating_min: b.rating_min, rating_max: b.rating_max,
    rating_min_label: b.rating_min_label || null, rating_max_label: b.rating_max_label || null,
  };
}

export default function PBSurveyAdmin() {
  const { profile, loading } = useProfile();
  const [tab, setTab] = useState<Tab>('surveys');
  const [editing, setEditing] = useState<PulseSurvey | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  if (loading) return <LoadingScreen />;
  if (!profile?.is_super_admin) {
    return <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui' }}><Banner type="err" text="This area is restricted to the Altveno admin team." /></div>;
  }

  const goCreate = (survey: PulseSurvey | null) => { setEditing(survey); setTab('create'); };

  return (
    <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ marginBottom: '18px' }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>Survey Admin</h1>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Create, distribute and analyse PulseConnect surveys</p>
      </div>

      <div style={{ display: 'flex', gap: '2px', borderBottom: `1px solid ${C.border}`, marginBottom: '22px' }}>
        {([['surveys', 'Surveys'], ['create', editing ? 'Edit' : 'Create'], ['templates', 'Templates'], ['results', 'Results']] as [Tab, string][]).map(([key, lbl]) => (
          <button key={key} onClick={() => { if (key !== 'create') setEditing(null); setTab(key); }} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '10px 16px',
            fontSize: '13px', fontFamily: 'system-ui', fontWeight: tab === key ? 600 : 500,
            color: tab === key ? C.navy : C.muted,
            borderBottom: tab === key ? `2px solid ${C.navy}` : '2px solid transparent', marginBottom: '-1px',
          }}>{lbl}</button>
        ))}
      </div>

      {tab === 'surveys' && <SurveysTab key={refreshKey} onEdit={goCreate} onChanged={() => setRefreshKey(k => k + 1)} />}
      {tab === 'create' && <CreateTab editing={editing} onSaved={() => { setEditing(null); setRefreshKey(k => k + 1); setTab('surveys'); }} />}
      {tab === 'templates' && <TemplatesTab />}
      {tab === 'results' && <ResultsTab />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  SURVEYS TAB
// ════════════════════════════════════════════════════════════════════════════
function SurveysTab({ onEdit, onChanged }: { onEdit: (s: PulseSurvey) => void; onChanged: () => void }) {
  const [rows, setRows] = useState<SurveyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState('');

  useEffect(() => { load(); }, []);
  async function load() {
    setLoading(true);
    const { data: surveys } = await supabase.from('pulse_surveys').select('*').order('created_at', { ascending: false });
    const list = (surveys as PulseSurvey[]) || [];
    const rowsBuilt: SurveyRow[] = [];
    for (const s of list) {
      const { count: assignmentCount } = await supabase.from('pulse_assignments').select('*', { count: 'exact', head: true }).eq('survey_id', s.id);
      const { count: responseCount } = await supabase.from('pulse_responses').select('*', { count: 'exact', head: true }).eq('survey_id', s.id);
      const { count: completeCount } = await supabase.from('pulse_responses').select('*', { count: 'exact', head: true }).eq('survey_id', s.id).eq('is_complete', true);
      rowsBuilt.push({ ...s, assignmentCount: assignmentCount || 0, responseCount: responseCount || 0, completeCount: completeCount || 0 });
    }
    setRows(rowsBuilt);
    setLoading(false);
  }

  async function resolveTargets(s: PulseSurvey): Promise<string[]> {
    if (s.target_audience === 'specific') return s.target_profile_ids || [];
    const roles = s.target_audience === 'both' ? ['manager', 'allocator'] : s.target_audience === 'managers' ? ['manager'] : ['allocator'];
    const { data } = await supabase.from('profiles').select('id').in('role', roles).eq('account_status', 'approved');
    return (data || []).map((p: any) => p.id);
  }

  async function publish(s: SurveyRow) {
    setBusy(s.id); setMsg('');
    const targets = await resolveTargets(s);
    if (targets.length) {
      await supabase.from('pulse_assignments').upsert(targets.map(pid => ({ survey_id: s.id, profile_id: pid })), { onConflict: 'survey_id,profile_id', ignoreDuplicates: true });
    }
    await supabase.from('pulse_surveys').update({ status: 'published', updated_at: new Date().toISOString() }).eq('id', s.id);
    // notify only those not yet notified
    const { data: fresh } = await supabase.from('pulse_assignments').select('id, profile_id').eq('survey_id', s.id).is('notified_at', null);
    const freshIds = (fresh || []).map((a: any) => a.profile_id);
    if (freshIds.length) {
      await notifySurveyPublished(s, freshIds);
      await supabase.from('pulse_assignments').update({ notified_at: new Date().toISOString() }).in('id', (fresh || []).map((a: any) => a.id));
    }
    setMsg(`Published to ${targets.length} participant${targets.length !== 1 ? 's' : ''}.`);
    setBusy(null); await load(); onChanged();
  }

  async function close(s: SurveyRow) {
    setBusy(s.id);
    await supabase.from('pulse_surveys').update({ status: 'closed', updated_at: new Date().toISOString() }).eq('id', s.id);
    setBusy(null); await load(); onChanged();
  }

  async function publishResults(s: SurveyRow) {
    setBusy(s.id); setMsg('');
    await supabase.from('pulse_surveys').update({ results_published: true, results_published_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq('id', s.id);
    const { data: assigned } = await supabase.from('pulse_assignments').select('profile_id').eq('survey_id', s.id);
    const ids = (assigned || []).map((a: any) => a.profile_id);
    if (ids.length) await notifyResultsPublished(s, ids);
    setMsg('Results published, participants notified.');
    setBusy(null); await load(); onChanged();
  }

  async function del(s: SurveyRow) {
    if (!window.confirm(`Delete "${s.title}" and all its responses? This cannot be undone.`)) return;
    setBusy(s.id);
    await supabase.from('pulse_surveys').delete().eq('id', s.id);
    setBusy(null); await load(); onChanged();
  }

  if (loading) return <div style={{ color: C.muted, fontSize: '13px' }}>Loading surveys…</div>;

  return (
    <div style={{ maxWidth: '980px' }}>
      {msg && <Banner type="ok" text={msg} />}
      {rows.length === 0 ? (
        <div style={{ ...card, padding: '40px', textAlign: 'center', fontSize: '13px', color: C.muted }}>No surveys yet. Use the Create tab to build one.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {rows.map(s => {
            const rate = s.assignmentCount ? Math.round((s.completeCount / s.assignmentCount) * 100) : 0;
            return (
              <div key={s.id} style={{ ...card, padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '260px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '5px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '14.5px', fontWeight: 600, color: C.navy }}>{s.title}</span>
                      <StatusBadge status={s.status} />
                      {s.results_published && <span style={{ background: 'rgba(201,168,76,0.12)', color: C.amber, border: '1px solid rgba(201,168,76,0.35)', fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '2px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>Results Live</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '11.5px', color: C.muted }}>
                      <span>{AUDIENCE_OPTIONS.find(a => a.value === s.target_audience)?.label}</span>
                      <span>{s.completeCount}/{s.assignmentCount} complete · {rate}%</span>
                      {s.deadline && <span>Deadline {fmtDate(s.deadline)}</span>}
                      <span>Created {fmtDate(s.created_at)}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <button onClick={() => onEdit(s)} disabled={busy === s.id} style={btnGhost}>Edit</button>
                    {s.status === 'draft' && <button onClick={() => publish(s)} disabled={busy === s.id} style={btnPrimary}>{busy === s.id ? '…' : 'Publish'}</button>}
                    {s.status === 'published' && <button onClick={() => close(s)} disabled={busy === s.id} style={btnGhost}>Close</button>}
                    {(s.status === 'published' || s.status === 'closed') && !s.results_published && <button onClick={() => publishResults(s)} disabled={busy === s.id} style={btnGold}>Publish Results</button>}
                    <button onClick={() => del(s)} disabled={busy === s.id} style={{ ...btnGhost, color: C.danger, borderColor: '#F3C6C9' }}>Delete</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  CREATE / EDIT TAB
// ════════════════════════════════════════════════════════════════════════════
function CreateTab({ editing, onSaved }: { editing: PulseSurvey | null; onSaved: () => void }) {
  const { user } = useAuth();
  const [title, setTitle] = useState(editing?.title || '');
  const [description, setDescription] = useState(editing?.description || '');
  const [audience, setAudience] = useState<TargetAudience>(editing?.target_audience || 'both');
  const [deadline, setDeadline] = useState(editing?.deadline ? editing.deadline.slice(0, 10) : '');
  const [showResults, setShowResults] = useState(editing?.show_results_after_completion ?? true);
  const [conferenceName, setConferenceName] = useState(editing?.conference_name || '');
  const [templateType, setTemplateType] = useState<string | null>(editing?.template_type || null);
  const [specificIds, setSpecificIds] = useState<string[]>(editing?.target_profile_ids || []);
  const [questions, setQuestions] = useState<BQ[]>([]);
  const [templates, setTemplates] = useState<PulseTemplate[]>([]);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const dragIdx = useRef<number | null>(null);

  useEffect(() => {
    supabase.from('pulse_templates').select('*').order('name').then(({ data }) => setTemplates((data as PulseTemplate[]) || []));
    if (editing) {
      supabase.from('pulse_questions').select('*').eq('survey_id', editing.id).order('order_index')
        .then(({ data }) => setQuestions((data || []).map(normalizeQuestion).map(toBQ)));
    }
  }, [editing]);

  const addQuestion = (type: QuestionType) => { setQuestions(q => [...q, blankQuestion(type)]); setAddOpen(false); };
  const updateQ = (localId: string, patch: Partial<BQ>) => setQuestions(qs => qs.map(q => q.localId === localId ? { ...q, ...patch } : q));
  const removeQ = (localId: string) => setQuestions(qs => qs.filter(q => q.localId !== localId));
  const moveQ = (from: number, to: number) => {
    if (to < 0 || to >= questions.length) return;
    setQuestions(qs => { const n = [...qs]; const [it] = n.splice(from, 1); n.splice(to, 0, it); return n; });
  };

  function loadTemplate(t: PulseTemplate) {
    const qs: BQ[] = (t.questions || []).map((raw: any) => ({ ...blankQuestion(raw.question_type), ...toBQ(normalizeQuestion({ ...raw, id: nextId(), survey_id: '' })) }));
    setQuestions(qs);
    setTemplateType(t.template_type);
    if (!title) setTitle(t.name);
    if (!description) setDescription(t.description || '');
  }

  async function save(publish: boolean) {
    setErr('');
    if (!title.trim()) { setErr('Please give the survey a title.'); return; }
    if (!questions.length) { setErr('Add at least one question.'); return; }
    if (questions.some(q => !q.question_text.trim())) { setErr('Every question needs question text.'); return; }
    setSaving(true);

    const payload: any = {
      title: title.trim(), description: description.trim() || null, target_audience: audience,
      target_profile_ids: audience === 'specific' ? specificIds : [], deadline: deadline ? new Date(deadline).toISOString() : null,
      show_results_after_completion: showResults, template_type: templateType, conference_name: conferenceName.trim() || null,
      updated_at: new Date().toISOString(),
    };

    let surveyId = editing?.id;
    if (editing) {
      await supabase.from('pulse_surveys').update(payload).eq('id', editing.id);
    } else {
      const { data, error } = await supabase.from('pulse_surveys').insert({ ...payload, created_by: user!.id, status: 'draft' }).select().single();
      if (error) { setErr(error.message); setSaving(false); return; }
      surveyId = data.id;
    }

    // Replace questions.
    await supabase.from('pulse_questions').delete().eq('survey_id', surveyId);
    const rows = questions.map((q, i) => ({
      survey_id: surveyId, order_index: i, question_type: q.question_type, question_text: q.question_text.trim(),
      description: q.description.trim() || null, required: q.required, options: q.options,
      matrix_rows: q.matrix_rows, matrix_columns: q.matrix_columns, rating_min: q.rating_min, rating_max: q.rating_max,
      rating_min_label: q.rating_min_label || null, rating_max_label: q.rating_max_label || null,
    }));
    await supabase.from('pulse_questions').insert(rows);

    if (publish) {
      // resolve + assign + notify
      let targets: string[] = [];
      if (audience === 'specific') targets = specificIds;
      else {
        const roles = audience === 'both' ? ['manager', 'allocator'] : audience === 'managers' ? ['manager'] : ['allocator'];
        const { data } = await supabase.from('profiles').select('id').in('role', roles).eq('account_status', 'approved');
        targets = (data || []).map((p: any) => p.id);
      }
      if (targets.length) await supabase.from('pulse_assignments').upsert(targets.map(pid => ({ survey_id: surveyId, profile_id: pid })), { onConflict: 'survey_id,profile_id', ignoreDuplicates: true });
      await supabase.from('pulse_surveys').update({ status: 'published' }).eq('id', surveyId);
      const { data: fresh } = await supabase.from('pulse_assignments').select('id, profile_id').eq('survey_id', surveyId).is('notified_at', null);
      const freshIds = (fresh || []).map((a: any) => a.profile_id);
      if (freshIds.length) {
        await notifySurveyPublished({ ...payload, id: surveyId } as PulseSurvey, freshIds);
        await supabase.from('pulse_assignments').update({ notified_at: new Date().toISOString() }).in('id', (fresh || []).map((a: any) => a.id));
      }
    }
    setSaving(false);
    onSaved();
  }

  return (
    <div style={{ maxWidth: '840px' }}>
      {err && <Banner type="err" text={err} />}

      {/* Metadata */}
      <div style={{ ...card, padding: '20px 22px', marginBottom: '16px' }}>
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>Survey Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Q3 2026 Market Sentiment" style={inputStyle} />
        </div>
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Optional context shown to participants" style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '14px' }}>
          <div>
            <label style={labelStyle}>Deadline</label>
            <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Load Template</label>
            <select value="" onChange={e => { const t = templates.find(x => x.id === e.target.value); if (t) loadTemplate(t); }} style={inputStyle}>
              <option value="">Select a template…</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>

        {/* Target audience */}
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>Target Audience</label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {AUDIENCE_OPTIONS.map(a => (
              <button key={a.value} onClick={() => setAudience(a.value)} style={{
                padding: '7px 14px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer', fontFamily: 'system-ui', fontWeight: 500,
                border: `1px solid ${audience === a.value ? C.navy : C.border}`, background: audience === a.value ? C.navy : C.white, color: audience === a.value ? C.white : C.textSec,
              }}>{a.label}</button>
            ))}
          </div>
        </div>
        {audience === 'specific' && <SpecificSelector selected={specificIds} onChange={setSpecificIds} />}

        {(templateType === 'post_conference') && (
          <div style={{ marginTop: '14px' }}>
            <label style={labelStyle}>Conference Name</label>
            <input value={conferenceName} onChange={e => setConferenceName(e.target.value)} placeholder="e.g. Altveno Global Summit 2026" style={inputStyle} />
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: '9px', marginTop: '14px', cursor: 'pointer', fontSize: '12.5px', color: C.textSec }}>
          <input type="checkbox" checked={showResults} onChange={e => setShowResults(e.target.checked)} />
          Allow participants to view results after completion (once published)
        </label>
      </div>

      {/* Questions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: C.navy }}>Questions ({questions.length})</span>
        <button onClick={() => setPreview(true)} disabled={!questions.length} style={btnGhost}>Preview</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {questions.map((q, i) => (
          <div key={q.localId}
            draggable onDragStart={() => { dragIdx.current = i; }} onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); if (dragIdx.current !== null) moveQ(dragIdx.current, i); dragIdx.current = null; }}
            style={{ ...card, padding: '16px 18px' }}>
            <QuestionEditor q={q} index={i} onChange={patch => updateQ(q.localId, patch)} onRemove={() => removeQ(q.localId)} onMove={dir => moveQ(i, i + dir)} />
          </div>
        ))}
      </div>

      {/* Add question */}
      <div style={{ position: 'relative', marginTop: '12px' }}>
        <button onClick={() => setAddOpen(o => !o)} style={{ ...btnGhost, borderStyle: 'dashed', width: '100%', padding: '11px' }}>+ Add Question</button>
        {addOpen && (
          <div style={{ ...card, position: 'absolute', top: '44px', left: 0, right: 0, zIndex: 10, padding: '6px' }}>
            {QUESTION_TYPES.map(t => (
              <button key={t.value} onClick={() => addQuestion(t.value)} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '9px 12px', borderRadius: '3px', fontFamily: 'system-ui' }}
                onMouseEnter={e => (e.currentTarget.style.background = C.surface)} onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: C.navy }}>{t.label}</span>
                <span style={{ fontSize: '11.5px', color: C.muted, marginLeft: '8px' }}>{t.hint}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Save actions */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '22px', paddingTop: '18px', borderTop: `1px solid ${C.border}` }}>
        <button onClick={() => save(false)} disabled={saving} style={btnGhost}>{saving ? 'Saving…' : 'Save as Draft'}</button>
        <button onClick={() => save(true)} disabled={saving} style={btnGold}>{saving ? 'Saving…' : editing ? 'Save & Publish' : 'Publish'}</button>
      </div>

      {preview && <PreviewModal title={title} description={description} questions={questions} onClose={() => setPreview(false)} />}
    </div>
  );
}

// ── Per-question editor ──────────────────────────────────────────────────────
function QuestionEditor({ q, index, onChange, onRemove, onMove }: {
  q: BQ; index: number; onChange: (p: Partial<BQ>) => void; onRemove: () => void; onMove: (dir: number) => void;
}) {
  const typeLabel = QUESTION_TYPES.find(t => t.value === q.question_type)?.label || q.question_type;
  const hasOptions = q.question_type === 'multiple_choice' || q.question_type === 'multi_select' || q.question_type === 'ranking';
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <span style={{ cursor: 'grab', color: C.mutedLt, fontSize: '15px' }}>⋮⋮</span>
        <span style={{ fontSize: '11px', fontWeight: 700, color: C.gold, fontFamily: 'Georgia, serif' }}>Q{index + 1}</span>
        <select value={q.question_type} onChange={e => onChange({ ...blankFieldsForType(e.target.value as QuestionType), question_type: e.target.value as QuestionType })}
          style={{ ...inputStyle, width: 'auto', padding: '5px 8px', fontSize: '11.5px' }}>
          {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', color: C.textSec, cursor: 'pointer' }}>
            <input type="checkbox" checked={q.required} onChange={e => onChange({ required: e.target.checked })} /> Required
          </label>
          <button onClick={() => onMove(-1)} style={iconBtn} title="Move up">↑</button>
          <button onClick={() => onMove(1)} style={iconBtn} title="Move down">↓</button>
          <button onClick={onRemove} style={{ ...iconBtn, color: C.danger }} title="Delete">✕</button>
        </div>
      </div>

      <input value={q.question_text} onChange={e => onChange({ question_text: e.target.value })} placeholder="Question text" style={{ ...inputStyle, marginBottom: '8px', fontWeight: 500 }} />
      <input value={q.description} onChange={e => onChange({ description: e.target.value })} placeholder="Description / helper text (optional)" style={{ ...inputStyle, marginBottom: '10px', fontSize: '12px' }} />

      {hasOptions && <StringListEditor label={q.question_type === 'ranking' ? 'Items to rank' : 'Options'} items={q.options} onChange={items => onChange({ options: items })} />}

      {q.question_type === 'rating' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
          <div><label style={labelStyle}>Min</label><input type="number" value={q.rating_min} onChange={e => onChange({ rating_min: parseInt(e.target.value, 10) || 0 })} style={inputStyle} /></div>
          <div><label style={labelStyle}>Max</label><input type="number" value={q.rating_max} onChange={e => onChange({ rating_max: parseInt(e.target.value, 10) || 5 })} style={inputStyle} /></div>
          <div><label style={labelStyle}>Min Label</label><input value={q.rating_min_label} onChange={e => onChange({ rating_min_label: e.target.value })} placeholder="e.g. Poor" style={inputStyle} /></div>
          <div><label style={labelStyle}>Max Label</label><input value={q.rating_max_label} onChange={e => onChange({ rating_max_label: e.target.value })} placeholder="e.g. Excellent" style={inputStyle} /></div>
        </div>
      )}

      {q.question_type === 'nps' && <div style={{ fontSize: '11.5px', color: C.muted, background: C.surface, padding: '8px 12px', borderRadius: '3px' }}>NPS is auto-configured as an 0–10 recommendation scale.</div>}
      {q.question_type === 'free_text' && <div style={{ fontSize: '11.5px', color: C.muted, background: C.surface, padding: '8px 12px', borderRadius: '3px' }}>Participants respond with open text.</div>}

      {q.question_type === 'matrix' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <StringListEditor label="Rows (statements)" items={q.matrix_rows} onChange={items => onChange({ matrix_rows: items })} />
          <StringListEditor label="Columns (scale)" items={q.matrix_columns} onChange={items => onChange({ matrix_columns: items })} />
        </div>
      )}
    </div>
  );
}

function blankFieldsForType(type: QuestionType): Partial<BQ> {
  const b = blankQuestion(type);
  return { options: b.options, matrix_rows: b.matrix_rows, matrix_columns: b.matrix_columns };
}

const iconBtn: React.CSSProperties = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: '3px', width: '26px', height: '26px', cursor: 'pointer', fontSize: '12px', color: C.textSec, lineHeight: 1 };

function StringListEditor({ label, items, onChange }: { label: string; items: string[]; onChange: (i: string[]) => void }) {
  const dragIdx = useRef<number | null>(null);
  const set = (i: number, v: string) => onChange(items.map((x, idx) => idx === i ? v : x));
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, `Option ${items.length + 1}`]);
  const move = (from: number, to: number) => { if (to < 0 || to >= items.length) return; const n = [...items]; const [it] = n.splice(from, 1); n.splice(to, 0, it); onChange(n); };
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        {items.map((it, i) => (
          <div key={i} draggable onDragStart={() => { dragIdx.current = i; }} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); if (dragIdx.current !== null) move(dragIdx.current, i); dragIdx.current = null; }}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ cursor: 'grab', color: C.mutedLt, fontSize: '13px' }}>⋮⋮</span>
            <input value={it} onChange={e => set(i, e.target.value)} style={{ ...inputStyle, padding: '6px 9px', fontSize: '12.5px' }} />
            <button onClick={() => remove(i)} style={{ ...iconBtn, flexShrink: 0 }} title="Remove">✕</button>
          </div>
        ))}
      </div>
      <button onClick={add} style={{ ...btnGhost, marginTop: '6px', padding: '5px 12px', fontSize: '11.5px' }}>+ Add</button>
    </div>
  );
}

// ── Specific-individuals selector ────────────────────────────────────────────
function SpecificSelector({ selected, onChange }: { selected: string[]; onChange: (ids: string[]) => void }) {
  const [profiles, setProfiles] = useState<ProfileLite[]>([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'manager' | 'allocator'>('all');

  useEffect(() => {
    supabase.from('profiles').select('id, full_name, email, role').in('role', ['manager', 'allocator']).eq('account_status', 'approved').order('full_name')
      .then(({ data }) => setProfiles((data as ProfileLite[]) || []));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return profiles.filter(p => (roleFilter === 'all' || p.role === roleFilter) && (!q || [p.full_name, p.email].filter(Boolean).some(v => String(v).toLowerCase().includes(q))));
  }, [profiles, search, roleFilter]);

  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);

  return (
    <div style={{ marginTop: '12px', border: `1px solid ${C.border}`, borderRadius: '4px', padding: '12px' }}>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search people…" style={{ ...inputStyle, maxWidth: '260px', padding: '6px 10px', fontSize: '12.5px' }} />
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value as any)} style={{ ...inputStyle, width: 'auto', padding: '6px 10px', fontSize: '12.5px' }}>
          <option value="all">All roles</option><option value="manager">Managers</option><option value="allocator">Allocators</option>
        </select>
        <span style={{ marginLeft: 'auto', fontSize: '11.5px', color: C.muted, alignSelf: 'center', fontWeight: 600 }}>{selected.length} selected</span>
      </div>
      <div style={{ maxHeight: '220px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {filtered.map(p => {
          const on = selected.includes(p.id);
          return (
            <button key={p.id} onClick={() => toggle(p.id)} style={{
              display: 'flex', alignItems: 'center', gap: '9px', textAlign: 'left', cursor: 'pointer', padding: '7px 10px', borderRadius: '3px',
              border: `1px solid ${on ? C.navy : C.borderLt}`, background: on ? 'rgba(10,22,40,0.03)' : C.white, fontFamily: 'system-ui',
            }}>
              <span style={{ width: '15px', height: '15px', borderRadius: '3px', border: `2px solid ${on ? C.navy : C.mutedLt}`, background: on ? C.navy : C.white, color: C.white, fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{on && '✓'}</span>
              <span style={{ fontSize: '12.5px', color: C.text, fontWeight: 500 }}>{p.full_name || p.email}</span>
              <span style={{ fontSize: '10px', color: C.muted, textTransform: 'capitalize', marginLeft: 'auto' }}>{p.role}</span>
            </button>
          );
        })}
        {filtered.length === 0 && <div style={{ fontSize: '12px', color: C.mutedLt, padding: '10px', textAlign: 'center' }}>No matching people.</div>}
      </div>
    </div>
  );
}

// ── Preview modal ────────────────────────────────────────────────────────────
function PreviewModal({ title, description, questions, onClose }: { title: string; description: string; questions: BQ[]; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, maxWidth: '680px', width: '100%', padding: '32px 34px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: C.gold, letterSpacing: '1px', textTransform: 'uppercase' }}>Participant Preview</span>
          <button onClick={onClose} style={iconBtn}>✕</button>
        </div>
        <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '22px', color: C.black, fontWeight: 400, margin: '0 0 8px' }}>{title || 'Untitled survey'}</h2>
        {description && <p style={{ fontSize: '13px', color: C.textSec, lineHeight: 1.6, margin: '0 0 20px' }}>{description}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', pointerEvents: 'none' }}>
          {questions.map((q, i) => {
            const pq = bqToPreview(q, i);
            return (
              <div key={q.localId} style={{ ...card, padding: '18px 20px' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '12px' }}>{i + 1}. {pq.question_text}{q.required && <span style={{ color: C.danger }}> *</span>}</div>
                <QuestionInput question={pq} value={undefined} onChange={() => {}} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TEMPLATES TAB
// ════════════════════════════════════════════════════════════════════════════
function TemplatesTab() {
  const [templates, setTemplates] = useState<PulseTemplate[]>([]);
  const [surveys, setSurveys] = useState<PulseSurvey[]>([]);
  const [previewT, setPreviewT] = useState<PulseTemplate | null>(null);
  const [msg, setMsg] = useState('');
  const [fromSurvey, setFromSurvey] = useState('');
  const [newName, setNewName] = useState('');
  const [postConf, setPostConf] = useState(false);

  useEffect(() => { load(); }, []);
  async function load() {
    const { data: t } = await supabase.from('pulse_templates').select('*').order('name');
    setTemplates((t as PulseTemplate[]) || []);
    const { data: s } = await supabase.from('pulse_surveys').select('*').order('created_at', { ascending: false });
    setSurveys((s as PulseSurvey[]) || []);
  }

  async function createTemplate() {
    if (!fromSurvey || !newName.trim()) { setMsg(''); return; }
    const { data: qs } = await supabase.from('pulse_questions').select('*').eq('survey_id', fromSurvey).order('order_index');
    const questions = (qs || []).map(normalizeQuestion).map((q, i) => ({
      order_index: i + 1, question_type: q.question_type, question_text: q.question_text, description: q.description,
      required: q.required, options: q.options, matrix_rows: q.matrix_rows, matrix_columns: q.matrix_columns,
      rating_min: q.rating_min, rating_max: q.rating_max, rating_min_label: q.rating_min_label, rating_max_label: q.rating_max_label,
    }));
    await supabase.from('pulse_templates').insert({ name: newName.trim(), description: 'Custom template', template_type: postConf ? 'post_conference' : 'custom', questions });
    setNewName(''); setFromSurvey(''); setPostConf(false); setMsg('Template created.');
    await load();
  }

  return (
    <div style={{ maxWidth: '900px' }}>
      {msg && <Banner type="ok" text={msg} />}

      <div style={{ ...card, padding: '18px 20px', marginBottom: '20px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: C.navy, marginBottom: '12px' }}>Create template from an existing survey</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '10px' }}>
          <div>
            <label style={labelStyle}>Source survey</label>
            <select value={fromSurvey} onChange={e => setFromSurvey(e.target.value)} style={inputStyle}>
              <option value="">Select…</option>
              {surveys.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Template name</label>
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Quarterly Sentiment" style={inputStyle} />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: C.textSec, cursor: 'pointer', marginBottom: '12px' }}>
          <input type="checkbox" checked={postConf} onChange={e => setPostConf(e.target.checked)} /> Tag as post-conference (shown only to conference participants)
        </label>
        <button onClick={createTemplate} disabled={!fromSurvey || !newName.trim()} style={btnPrimary}>Create Template</button>
      </div>

      <div style={{ fontSize: '13px', fontWeight: 700, color: C.navy, marginBottom: '10px' }}>Templates ({templates.length})</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {templates.map(t => (
          <div key={t.id} style={{ ...card, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: C.navy }}>{t.name}</span>
                <span style={{ background: C.surface, color: C.muted, border: `1px solid ${C.border}`, fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '2px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{t.template_type.replace(/_/g, ' ')}</span>
              </div>
              <div style={{ fontSize: '11.5px', color: C.muted }}>{t.description} · {(t.questions || []).length} questions</div>
            </div>
            <button onClick={() => setPreviewT(t)} style={btnGhost}>Preview</button>
          </div>
        ))}
      </div>

      {previewT && (
        <PreviewModal title={previewT.name} description={previewT.description || ''}
          questions={(previewT.questions || []).map((raw: any) => toBQ(normalizeQuestion({ ...raw, id: nextId(), survey_id: '' })))}
          onClose={() => setPreviewT(null)} />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  RESULTS TAB
// ════════════════════════════════════════════════════════════════════════════
function ResultsTab() {
  const [surveys, setSurveys] = useState<PulseSurvey[]>([]);
  const [selected, setSelected] = useState('');

  useEffect(() => {
    supabase.from('pulse_surveys').select('*').order('created_at', { ascending: false })
      .then(({ data }) => { const list = (data as PulseSurvey[]) || []; setSurveys(list); if (list.length && !selected) setSelected(list[0].id); });
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      <div style={{ ...card, padding: '14px 18px', marginBottom: '18px', maxWidth: '520px' }}>
        <label style={labelStyle}>Select survey</label>
        <select value={selected} onChange={e => setSelected(e.target.value)} style={inputStyle}>
          <option value="">Select a survey…</option>
          {surveys.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
        </select>
      </div>
      {selected ? <ResultsDashboard surveyId={selected} isPB /> : <div style={{ ...card, padding: '40px', textAlign: 'center', fontSize: '13px', color: C.muted }}>Select a survey to view its results.</div>}
    </div>
  );
}
